import { cloneDeep } from 'lodash';
import convertKelvinToOrFromMired from '../../../utils/convertKelvinToOrFromMired';
import displayUnit from '../../../utils/displayUnit';

// maps kelvin to mired to nearest hundred
export const mapKelvinToMired = k =>
  Math.round(convertKelvinToOrFromMired(k) / 100) * 100;

const parseValue = value => {
  try {
    if (!value || typeof value !== 'string') return value;

    return JSON.parse(value);
  } catch (error) {
    console.error(`Could not parse ${value}. Error: ${error}`);
    return value;
  }
};

const compareXy = (reading1, reading2) => {
  const { x1, y1 } = parseValue(reading1);
  const { x2, y2 } = parseValue(reading2);

  return x1 === x2 && y1 === y2;
};

export const mapFeature = (initialFeature, sensors) => {
  const sensor = sensors.find(
    s => s.id.split('|')[1] === initialFeature.property,
  );
  const feature = cloneDeep(initialFeature);
  feature.mapValueDto = v => v;
  feature.equals = (reading1, reading2) => reading1 === reading2;

  if (sensor) {
    feature.reading = {
      value: sensor.reading,
      date: sensor.reading_date,
      unit: displayUnit(sensor.unit),
      status: sensor.status,
      threshold: sensor.threshold,
      measuring_mode: sensor.measuring_mode,
    };
  }

  if (initialFeature.name === 'color_xy') {
    feature.label = 'Color (RGB)';
    feature.description = 'Color of this light in RGB';
    feature.equals = compareXy;
  }

  if (initialFeature.type === 'numeric' && initialFeature.unit === 'mired') {
    if (initialFeature.presets) {
      feature.presets = initialFeature.presets
        .map(p => ({
          ...p,
          value: mapKelvinToMired(p.value),
        }))
        .sort((a, b) => a.value - b.value);
    }

    // interchange value_min with value_max since the units are inversely proportional
    feature.value_min = Math.floor(mapKelvinToMired(initialFeature.value_max));
    feature.value_max = Math.ceil(mapKelvinToMired(initialFeature.value_min));
    feature.mapValueDto = convertKelvinToOrFromMired;
    feature.unit = 'Kelvin';

    if (feature?.reading?.value) {
      feature.reading.value = Math.ceil(
        mapKelvinToMired(feature.reading.value),
      );
    }
  }

  return feature;
};

// helper to translate enums
const applyEnumTranslations = (feature, translations) => {
  // translations { en: pt }
  const enToPt = translations;
  const ptToEn = Object.fromEntries(
    Object.entries(translations).map(([en, pt]) => [pt, en]),
  );

  // translated values
  feature.values = Object.values(enToPt);
  // function to map back to original value
  feature.mapValueDto = v => ptToEn[v] ?? v;
};

/**
 * @todo [C20-224] Replace this hardcoded mapping with dynamic translation for zigbee parameters names and descriptions
 * @link https://corvalent.atlassian.net/browse/C20-224
 */
export const mapThermostatLabelsToPtBR = feature => {
  switch (feature.property) {
    case 'roomN':
      feature.label = 'Número do quarto';
      break;

    case 'state':
      feature.label = 'Estado (bruto)';
      applyEnumTranslations(feature, {
        cleaning: 'Em limpeza',
        ready: 'Pronto para uso',
        checkedOut: 'Desocupado',
        rentedEmpty: 'Alugado (vazio)',
        occupied: 'Ocupado',
      });
      break;

    case 'fanSpeed':
      feature.label = 'Velocidade do ventilador';
      applyEnumTranslations(feature, {
        High: 'Alta',
        Medium: 'Média',
        Low: 'Baixa',
        Auto: 'Automático',
      });
      break;

    case 'tmode':
      feature.label = 'Modo do sistema';
      applyEnumTranslations(feature, {
        Cool: 'Resfriar',
        Heat: 'Aquecer',
        Ventilation: 'Ventilação',
      });
      break;

    case 'tstatus':
      feature.label = 'Status do termostato';
      break;

    case 'tsetT':
      feature.label = 'Setpoint';
      break;

    case 'troomT':
      feature.label = 'Temperatura do ambiente';
      break;

    case 'toutpins':
      feature.label = 'Mapa de saídas';
      break;

    default:
      break;
  }

  return feature;
};
